import { memo, useCallback, useState, useEffect } from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";

import { Loader } from "components";

import { getCall_v2 } from "services";

import {
  formatTimelineDate,
  formatTimelineDateTime,
  getExpectedFgTotal,
} from "../makeData";

const FGHistory = ({
  materialId,
  materialDescription,
  orderedQty,
  orderId,
  lineItemId,
}) => {
  const [historyTimelineData, setHistoryTimelineData] = useState([]);
  const [historyTimelineLoading, setHistoryTimelineLoading] = useState(false);

  const fetchHistoryTimeLine = useCallback(async () => {
    try {
      setHistoryTimelineLoading(true);
      const res = await getCall_v2(
        `/joms/api/internal/v1/finished-goods/fetch-lineitem-fg-history?customerOrderId=${orderId}&lineitemId=${lineItemId}`,
      );
      setHistoryTimelineData(res?.data?.data || []);
    } catch (err) {
      console.error("Error fetching history timeline", err);
      setHistoryTimelineData([]);
    } finally {
      setHistoryTimelineLoading(false);
    }
  }, [lineItemId, orderId]);

  useEffect(() => {
    if (orderId && lineItemId) {
      void fetchHistoryTimeLine();
    }
  }, [fetchHistoryTimeLine, orderId, lineItemId]);

  return (
    <Box ml={2} mr={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        pt={1.5}
        pb={2.5}
      >
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
            {`FG History — ${materialId}`}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            mt={1}
            fontSize="14px"
          >
            {`${materialDescription} · ${orderedQty} ordered`}
          </Typography>
        </Box>
      </Stack>

      <Divider
        sx={{
          mb: 2,
          mt: 1,
          mx: -2,
          borderBottomWidth: "2px",
          borderColor: "grey.300",
        }}
      />

      <Box
        sx={{
          maxHeight: "65vh",
          overflowY: "auto",
          overflowX: "hidden",
          pr: 3,
        }}
      >
        <Box
          sx={{
            maxWidth: 760,
            py: 1,
          }}
        >
          <Typography
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              fontSize: "15px",
              letterSpacing: 1,
              mb: 4,
            }}
          >
            HISTORY TIMELINE
          </Typography>
          {historyTimelineLoading && (
            <Box
              sx={{
                height: "50px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Loader sx={{ marginTop: 0 }} />
            </Box>
          )}
          {!historyTimelineLoading && historyTimelineData.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No history available
            </Typography>
          )}
          <Box>
            {historyTimelineData.map((item, index) => {
              const fgReadyQty = Number(item.fgReadyQty || 0);
              const expectedFgTotal = getExpectedFgTotal(item.expectedFgQty);
              const hasExpectedFg = item.expectedFgQty?.length > 0;
              const isLastItem = index === historyTimelineData.length - 1;
              const summaryPrefix = isLastItem
                ? "Initial Expected FG commitment"
                : "FG Updated";
              return (
                <Box
                  key={`${item.updateBy}-${item.updatedAt}-${index}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "24px 1fr",
                    columnGap: 1.5,
                    position: "relative",
                    pb: index === historyTimelineData.length - 1 ? 0 : 3,
                  }}
                >
                  {index !== historyTimelineData.length - 1 && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 11,
                        top: 26,
                        bottom: -2,
                        borderLeft: "3px solid",
                        borderColor: "grey.300",
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      border: "3px solid",
                      borderColor: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "common.white",
                      mt: 0.4,
                      zIndex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                      }}
                    />
                  </Box>
                  <Box>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      mb={2}
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                          flex: "0 0 auto",
                        }}
                      />
                      <Typography sx={{ fontSize: 15 }} fontWeight={600}>
                        {item.updateBy?.trim() || "-"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {`· ${formatTimelineDateTime(item.updatedAt) || "-"}`}
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        border: "1px solid",
                        borderColor: "grey.400",
                        borderRadius: "10px",
                        bgcolor: "grey.50",
                        px: 2.5,
                        py: 3,
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        <Box component="span" fontWeight={600} color="grey.800">
                          {summaryPrefix}:
                        </Box>{" "}
                        {`${fgReadyQty} MT ready`}
                        {hasExpectedFg && `, ${expectedFgTotal} MT planned`}
                      </Typography>
                      {hasExpectedFg && (
                        <>
                          <Divider sx={{ my: 2.5 }} />
                          <Stack spacing={1}>
                            {item.expectedFgQty.map((expectedItem, fgIndex) => (
                              <Stack
                                key={`${expectedItem.quantity}-${expectedItem.readinessDate}-${fgIndex}`}
                                direction="row"
                                spacing={2}
                                alignItems="center"
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "primary.main",
                                    fontWeight: 700,
                                    width: 40,
                                  }}
                                >
                                  {`FG${fgIndex + 1}`}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {`${Number(expectedItem.quantity || 0)} MT`}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {`by ${formatTimelineDate(
                                    expectedItem.readinessDate,
                                  )}`}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

FGHistory.propTypes = {
  materialId: PropTypes.string,
  materialDescription: PropTypes.string,
  orderedQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lineItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default memo(FGHistory);
